import React, { useEffect, useState, useCallback } from "react";
import axios from "utils/axiosConfig";
import { useSelector } from "react-redux";
import uri from "../../features/config";
import AdminLayout from "../AdminLayout";
import { HiOutlineShieldCheck } from "react-icons/hi";
import { useToast } from "../../components/Toast";

const UserManagement = () => {
  const toast = useToast();
  const { user: currentUser } = useSelector((state) => state.auth);
  const [users, setUsers] = useState([]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${uri}/api/admin/users`, {
        headers: { "x-user-email": currentUser?.data?.email },
      });
      setUsers(response.data.data);
    } catch (error) {
      toast.error("Account synchronization failed");
    }
  }, [currentUser, toast]);

  useEffect(() => {
    if (currentUser?.data?.email) fetchUsers();
  }, [currentUser, fetchUsers]);

  const toggleRole = async (email, currentRole) => {
    const newRole = currentRole === "admin" ? "user" : "admin";
    if (!window.confirm(`Escalate/De-escalate ${email} to ${newRole}?`)) return;

    try {
      await axios.put(
        `${uri}/api/admin/users/${email}/role`,
        { role: newRole },
        {
          headers: { "x-user-email": currentUser?.data?.email },
        }
      );
      toast.success("Security clearance updated");
      fetchUsers();
    } catch (error) {
      toast.error("Clearance authorization failed");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-12 animate-fadeIn">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 relative">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-[10px] font-black text-amber-400 uppercase tracking-widest">
              Identity_Vault_Status: Locked
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
              Player <span className="text-cyan-400">Database</span>
            </h1>
            <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-[10px]">
              Advanced identity management and security clearance control.
            </p>
          </div>
        </header>

        <div className="bg-[#111827]/40 backdrop-blur-2xl rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02] border-b border-white/5 text-gray-500 text-[10px] font-black tracking-[0.3em] uppercase italic">
                  <th className="px-10 py-8">Player Identity</th>
                  <th className="px-10 py-8 text-center">Gamer Points</th>
                  <th className="px-10 py-8 text-center">System_Status</th>
                  <th className="px-10 py-8 text-center">Clearance_Level</th>
                  <th className="px-10 py-8 text-right">Security_Ops</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-xs font-bold">
                {users.map((u) => (
                  <tr
                    key={u.email}
                    className="hover:bg-white/[0.03] transition-all duration-300 group"
                  >
                    <td className="px-10 py-8">
                      <div className="flex items-center gap-6">
                        <div className="relative group/avatar">
                          <div className="absolute -inset-1 bg-gradient-to-tr from-cyan-500 to-purple-600 rounded-2xl blur opacity-20 group-hover/avatar:opacity-60 transition-opacity" />
                          <div className="relative h-14 w-14 rounded-2xl bg-[#0b0e14] border border-white/10 flex items-center justify-center font-black text-xl text-white italic shadow-inner">
                            {u.name?.[0] || u.email[0].toUpperCase()}
                          </div>
                        </div>
                        <div>
                          <p className="font-black text-white italic tracking-widest uppercase group-hover:text-cyan-400 transition-colors">
                            {u.name || "Anonymous_Player"}
                          </p>
                          <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-1">
                            {u.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-10 py-8 text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-black text-white italic text-base">
                          {u.balance?.toLocaleString() || 0}
                        </span>
                        <span className="text-[8px] text-cyan-500 font-black uppercase tracking-[0.2em] mt-1">
                          Available_PTS
                        </span>
                      </div>
                    </td>
                    <td className="px-10 py-8 text-center">
                      <div className="inline-flex items-center gap-3 px-3 py-1.5 bg-green-500/5 border border-green-500/10 rounded-xl">
                        <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse shadow-[0_0_8px_#22c55e]"></div>
                        <span className="text-[9px] font-black text-green-500 uppercase tracking-widest italic">
                          Online
                        </span>
                      </div>
                    </td>
                    <td className="px-10 py-8 text-center">
                      <span
                        className={`px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-[0.2em] italic border ${
                          u.role === "admin"
                            ? "bg-purple-500/10 text-purple-400 border-purple-500/20"
                            : "bg-white/5 text-gray-500 border-white/5"
                        }`}
                      >
                        {u.role || "Recruit"}
                      </span>
                    </td>
                    <td className="px-10 py-8">
                      <div className="flex items-center justify-end opacity-0 group-hover:opacity-100 transform translate-x-4 group-hover:translate-x-0 transition-all duration-500">
                        <button
                          onClick={() => toggleRole(u.email, u.role)}
                          className="flex items-center gap-3 px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-cyan-400 rounded-xl transition-all italic"
                        >
                          <HiOutlineShieldCheck size={18} />
                          <span>Authorize Access</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {users.length === 0 && (
            <div className="py-24 text-center">
              <p className="text-gray-500 font-black uppercase tracking-[0.3em] italic text-xs">
                No player identity records found.
              </p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default UserManagement;
